'use client';
import { useState, useEffect, useRef } from 'react';
import { challengeService } from '@/app/service/challengeService/challengeService';
import { postsService } from '@/app/service/postsService/postsService';
import ChallengeModel from '@/app/models/challenges/ChallengeModel';
import { PostTypeConfig } from '@/app/constants/typedef';
import { PostModel } from '../models/posts/Posts';
import utilsService from '../helpers/utilsService';

type DataModel = ChallengeModel | PostModel;

const usePostDetails = <T extends DataModel>(id: string | undefined, tab: string) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [firstLoading, setFirstLoading] = useState<boolean>(true);
    const [data, setData] = useState<T | null>(null);
    const [error, setError] = useState<any>(null);
    const [hasError, setHasError] = useState<boolean>(false);
    const loadingCurrent = useRef<boolean>(false);
    const abortControllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        if (id) {
            fetchData();
        }
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [id]);

    const fetchData = async () => {
        if (isLoading || loadingCurrent.current) return;

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        loadingCurrent.current = true;
        setIsLoading(true);
        setError(null);
        setHasError(false);

        try {
            let response: any = null;
            if (tab === PostTypeConfig.Challenges) {
                response = await challengeService.getChallengeDetails({ challengeId: id as string });
                setData(new ChallengeModel(response.data) as T);
            } else if (
                tab === PostTypeConfig.Posts || 
                tab === PostTypeConfig.Products ||
                tab === PostTypeConfig.fetchForMyChannel ||
                tab === PostTypeConfig.fetchForPartner
            ) {
                response = await postsService.getPostsDetails({ postId: id as string });
                setData(new PostModel(response.data) as T);
            }
            setFirstLoading(false);
        } catch (error: any) {
            setFirstLoading(false);
            const messageError = utilsService.getErrorMessage(error);
            if (error.name !== 'AbortError') {
                setError(messageError);
                setHasError(true);
            }
        } finally {
            setIsLoading(false);
            loadingCurrent.current = false;
        }
    };

    const updatePost = (updatedFields: Partial<T>) => {
        setData((prevData) => {
            if (!prevData) return null;
            return { ...prevData, ...updatedFields };
        });
    };

    return { isLoading, firstLoading, data, error, hasError, updatePost, refetch: fetchData };
};

export default usePostDetails;
